import { useContext, useEffect, useState } from 'react';
import { getCustomisationValue, updateCustomisationValue } from '../../../EditablePreview/EditablePreview';
import ModuleNumericalInput from '../../components/ModuleNumericalInput';
// import './CustomModule.scss';
import { changeNumericalFieldValue, changeStringFieldValue, changeValue_noType } from '../../ModuleList';
import ModuleTextInput from '../../components/ModuleTextInput';
import { ShowColorPicker } from '../../../../../../forms/ColorPicker/ColorPickerPopUp';
import { CampaignContext } from "../../../../../../../contexts/CampaignContext";

import AudioIcon from '../../../../WorkspaceAssets/ModuleIcons/Audio.png';
import { COLOUR_NAME_TO_COLOUR_HEX } from '../../../../../../../helpers/Colours';

import './AudioField.scss';

import WaveSurfer from 'wavesurfer.js';
import { IconJsxer } from '../../../../../../../helpers/IconHelper';
import { PANEL_CONTENT_TYPES, slideOutPanelControl } from '../../../../../../../containers/TimelineEditor/slideout-panel/SlideoutPanel';
import { convertUniqueFilenameToOriginal } from '../../../../v2IntegrationHelpers/CampaignAndFeatureDataHelperFunctions';

const AudioField = (props) => {
    const fieldData = props.fieldData;
    const moduleConfig = props.moduleConfig;
    const groupConfig = props.groupConfig;
    const customisationVars = props.customisationVars;

    const [campaignContext, setCampaignContext] = useContext(CampaignContext);

    const [audioFileData, setAudioFileData] = useState(props.value);
    const pickerEnabled = props.isEnabled || fieldData?.customFieldConfig?.alwaysEnabled === true;
    useEffect(() => {
        setAudioFileData(props.value);
    }, [customisationVars, fieldData, props.index, props.visualisationComponentKey, props.selectedFeatureNode, props.value, props.forceUpdate]);

    const updateReceived = (updatedAudioFileData) => {
        // Make a copy of this data to avoid mutating props
        const updatedValCopy = Object.assign({}, updatedAudioFileData);
        changeValue_noType(
            customisationVars,
            fieldData?.customFieldConfig,
            updatedValCopy,
            props.index ?? 0,
            props.visualisationComponentKey ?? null,
            props.selectedFeatureNode,
            true,
            false
        );
        setAudioFileData(updatedValCopy);
    };

    const fileChangeClickHandler = () => {
        // A visible AudioUpload field is always a valid picker target. Its
        // visibility is controlled by the field's display condition; tying the
        // click handler to a second enabled flag can leave Replace Audio inert.
        if (!slideOutPanelControl.openAudioUploader) return;
        slideOutPanelControl.openAudioUploader(
            Object.assign({}, audioFileData),
            (updatedAudioFileData) => { /* console.log('AudioField: Audio file changed callback'); */ updateReceived(updatedAudioFileData); },
            (updatedAudioFileData) => { /* console.log('AudioField: Audio file change confirmed, prob not needed for this one...'); */ },
            'Upload Audio',
            fieldData?.customFieldConfig,
            props.previewVolumePercent ?? 100
        );
    }

    useEffect(() => {
        // console.log('AudioField: Detected audioFileData change, checking if audio uploader panel is open for this field...', slideOutPanelControl, fieldData);
        if (slideOutPanelControl.panelState.isOpen && slideOutPanelControl.panelState.type === PANEL_CONTENT_TYPES.AUDIO_UPLOADER && slideOutPanelControl.panelState.fieldData?.dataLink === fieldData?.customFieldConfig?.dataLink) {
            // If the audio uploader panel is open for this field, refresh it with the new data
            // console.log('AudioField: Detected audioFileData change while audio uploader panel is open, refreshing panel with new data:', audioFileData);
            // Trigger audio uploader slide out panel
            fileChangeClickHandler();
        }
    }, [audioFileData]);

    const displayAudioFilename = typeof audioFileData === 'string'
        ? audioFileData
        : convertUniqueFilenameToOriginal(audioFileData?.originalFile || audioFileData?.filename || '') || '<No Audio File>';
    const audioFilenameWithSourceLabel = audioFileData?.assetSourceType === 'template'
        ? `${displayAudioFilename} (Template File)`
        : displayAudioFilename;

    const waveformId = 'wf_' + props.selectedFeatureNode.id + fieldData?.customFieldConfig?.dataLink?.replace(/\./g, '_') + `_waveform_preview_${props.index ?? 0}_${props.visualisationComponentKey ?? 'default'}`;

    const [wavesurferInstance, setWavesurferInstance] = useState(null);
    useEffect(() => {
        if (audioFileData && audioFileData?.srcUrl) {
            // console.log('[timeline, useEffect] Creating waveform for', waveformId, 'with URL', audioFileData.srcUrl);
            const waveSurfer = WaveSurfer.create({
                container: `#${waveformId}`,
                waveColor: '#BCA4FF',
                progressColor: '#9146FF',
                url: audioFileData.srcUrl,
                height: 20,
                normalize: true,
                hideScrollbar: true,
            });
            setWavesurferInstance(waveSurfer);
            return () => {
                // console.log('[timeline, useEffect] Destroying waveform for', generatedWaveformContainerId);
                waveSurfer.destroy();
                setWavesurferInstance(null);
            }
        }
    }, [audioFileData?.srcUrl, waveformId]);

    const [isPlaying, setIsPlaying] = useState(false);
    useEffect(() => {
        if (wavesurferInstance) {
            const playHandler = () => { setIsPlaying(true); };
            const stopHandler = () => { setIsPlaying(false); };
            wavesurferInstance.on('play', playHandler);
            wavesurferInstance.on('finish', stopHandler);
            wavesurferInstance.on('pause', stopHandler);
        }
        return () => {
            if (wavesurferInstance) {
                wavesurferInstance.un('play');
                wavesurferInstance.un('finish');
                wavesurferInstance.un('pause');
                wavesurferInstance.stop(); // Ensure audio is stopped when component unmounts or wavesurfer instance changes
                wavesurferInstance.destroy(); // Clean up wavesurfer instance
            }
            setIsPlaying(false);
        };
    }, [wavesurferInstance]);

    return (
        <>
            <div className={`v2-module-field-label${!props.isEnabled ? ' disabled' : ''}`}>{fieldData?.customFieldConfig?.customFieldLabel ?? 'Audio File'}</div>
            <div className='fl-column compact'>
                <ModuleTextInput
                    fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: 'transparent', backgroundImage: `url(${AudioIcon})`, backgroundSize: '1.8rem', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', borderWidth: 0}} />}
                    value={audioFilenameWithSourceLabel}
                    fieldId={'audio-file'}
                    enabled={pickerEnabled}
                    editable={false}
                    onChange={null}
                    onClick={
                        pickerEnabled ? fileChangeClickHandler : null
                    }
                    squareBottom={false}
                />
            
                <div className='fl-row compact'>
                    <div className={`play-button-square${wavesurferInstance ? '' : ' btn-off'}`} onClick={wavesurferInstance ? () => {
                        const rawVolume = typeof props.previewVolumePercent === 'number'
                        ? props.previewVolumePercent
                        : Number(props.previewVolumePercent);
                        const resolvedVolume = Number.isFinite(rawVolume) ? rawVolume : 100;
                        wavesurferInstance.setVolume(Math.max(0, Math.min(1, resolvedVolume / 100)));
                        wavesurferInstance.isPlaying() ? wavesurferInstance.stop() : wavesurferInstance.play();
                    } : null}>
                        {IconJsxer.GetIcon(isPlaying ? IconJsxer.ICONS.stop : IconJsxer.ICONS.play, IconJsxer.ICON_STYLES.campaignPanelInfoPill, "icon-jsxer-on-purple-background")}
                    </div>
                    <div id={`${waveformId}`} className={`audio-waveform-preview-holder${!props.isEnabled ? ' v2-module-disabled-field' : ''}`}>
                        {/* Waveform preview will be rendered here */}
                    </div>
                </div>
            </div>
            <div className='pill-button' onClick={pickerEnabled ? fileChangeClickHandler : null}>Replace Audio</div>
        </>
    );
};

export default AudioField;
